import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalNav from '@/components/feature/PortalNav';
import { mockUser, mockMaterials } from '@/mocks/materials';

export default function AccountPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'history'>('overview');
  const [editMode, setEditMode] = useState(false);
  const [displayName, setDisplayName] = useState(mockUser.name);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [draftName, setDraftName] = useState(mockUser.name);
  const [draftAvatar, setDraftAvatar] = useState<string | null>(null);
  const [avatarHover, setAvatarHover] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const daysUntilExpiry = () => {
    const expiry = new Date(mockUser.expiryDate);
    const today = new Date();
    return Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };
  const days = daysUntilExpiry();
  const downloadedMaterials = mockMaterials.filter((m) => m.downloadAllowed).slice(0, 5);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setDraftAvatar(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleEditStart = () => {
    setDraftName(displayName);
    setDraftAvatar(avatarPreview);
    setEditMode(true);
  };

  const handleSave = () => {
    setDisplayName(draftName.trim() || displayName);
    setAvatarPreview(draftAvatar);
    setEditMode(false);
  };

  const handleCancel = () => {
    setDraftName(displayName);
    setDraftAvatar(avatarPreview);
    setEditMode(false);
  };

  const currentAvatar = editMode ? draftAvatar : avatarPreview;

  return (
    <div className="min-h-screen bg-[#f8f8f7]">
      <PortalNav showSearch={false} />

      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">

        {/* Profile Card */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-6">
          <div className="px-6 py-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">

              {/* Avatar */}
              <div className="relative shrink-0 w-20 h-20">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold border-2 border-white overflow-hidden cursor-pointer relative"
                  style={{ background: '#0069BD' }}
                  onMouseEnter={() => setAvatarHover(true)}
                  onMouseLeave={() => setAvatarHover(false)}
                  onClick={() => editMode && fileInputRef.current?.click()}
                >
                  {currentAvatar ? (
                    <img src={currentAvatar} alt="avatar" className="w-full h-full object-cover object-top" />
                  ) : (
                    <span>{mockUser.avatar}</span>
                  )}
                  {editMode && avatarHover && (
                    <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center rounded-full">
                      <i className="ri-camera-line text-white text-lg"></i>
                      <span className="text-white text-[10px] font-semibold mt-0.5">Change</span>
                    </div>
                  )}
                </div>
                {editMode && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute -bottom-1.5 -right-1.5 w-6 h-6 flex items-center justify-center bg-gray-800 text-white rounded-full cursor-pointer hover:bg-gray-700 transition-colors"
                  >
                    <i className="ri-pencil-line text-[10px]"></i>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
              </div>

              {/* Name + Email */}
              <div className="flex-1 min-w-0">
                {editMode ? (
                  <input
                    type="text"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    className="text-xl font-bold text-gray-900 border-b-2 border-gray-800 bg-transparent outline-none w-full max-w-xs mb-1 pb-0.5"
                    autoFocus
                  />
                ) : (
                  <h1 className="text-xl font-bold text-gray-900">{displayName}</h1>
                )}
                <p className="text-sm text-gray-400 mt-0.5">{mockUser.email}</p>
              </div>

              {/* Status + Edit Button */}
              <div className="flex flex-col items-start sm:items-end gap-2 shrink-0">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-xs font-semibold text-green-700">
                  <i className="ri-checkbox-circle-line text-xs"></i>
                  Active
                </span>
                {editMode ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleCancel}
                      className="px-3 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-3 py-1.5 text-xs font-semibold text-white bg-gray-900 rounded-lg hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Save Changes
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleEditStart}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-pencil-line text-xs"></i>
                    Edit Profile
                  </button>
                )}
              </div>
            </div>

            {/* Expiry Warning */}
            {days <= 30 && (
              <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl mt-5">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-100 shrink-0">
                  <i className="ri-error-warning-line text-amber-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-800">Account expires in {days} days</p>
                  <p className="text-xs text-amber-600">Please contact your Sinovision representative to renew.</p>
                </div>
              </div>
            )}

            {/* Info Stats */}
            <div className="grid grid-cols-4 gap-3 mt-5">
              {[
                { label: 'Company', value: mockUser.company, icon: 'ri-building-line', color: 'text-[#0069BD]' },
                { label: 'Region', value: mockUser.region, icon: 'ri-global-line', color: 'text-[#0069BD]' },
                { label: 'Member Since', value: mockUser.memberSince, icon: 'ri-calendar-check-line', color: 'text-[#0069BD]' },
                { label: 'Valid Until', value: mockUser.expiryDate, icon: 'ri-time-line', color: 'text-[#0069BD]' },
              ].map((stat) => (
                <div key={stat.label} className="bg-[#f8f8f7] rounded-xl p-3">
                  <div className="w-6 h-6 flex items-center justify-center mb-1.5">
                    <i className={`${stat.icon} text-sm`} style={{ color: '#0069BD' }}></i>
                  </div>
                  <p className="text-[11px] text-gray-400 mb-0.5">{stat.label}</p>
                  <p className="text-sm font-semibold text-gray-800">{stat.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex bg-white rounded-xl border border-gray-100 p-1 mb-6">
          {[
            { id: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
            { id: 'history', label: 'Download History', icon: 'ri-download-cloud-line' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id as 'overview' | 'history')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === t.id ? 'text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
              style={activeTab === t.id ? { background: '#0069BD' } : {}}
            >
              <i className={`${t.icon} text-sm`}></i>
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-flashlight-line text-gray-500"></i>
              Quick Actions
            </h3>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/library')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-200 text-gray-600">
                  <i className="ri-folder-line text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-gray-700">Browse Material Library</span>
                <i className="ri-arrow-right-line text-gray-400 ml-auto text-sm"></i>
              </button>
              <button className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-200 text-gray-600">
                  <i className="ri-mail-send-line text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-gray-700">Contact Support</span>
                <i className="ri-arrow-right-line text-gray-400 ml-auto text-sm"></i>
              </button>
              <button className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-200 text-gray-600">
                  <i className="ri-lock-password-line text-sm"></i>
                </div>
                <span className="text-sm font-semibold text-gray-700">Change Password</span>
                <i className="ri-arrow-right-line text-gray-400 ml-auto text-sm"></i>
              </button>
            </div>
          </div>
        )}

        {/* Download History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-800">Recent Downloads</h3>
            </div>
            <div className="divide-y divide-gray-50">
              {downloadedMaterials.map((m) => (
                <div key={m.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors">
                  <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-gray-100">
                    <img src={m.thumbnail} alt={m.title} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{m.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{m.type} · {m.fileSize} · Downloaded {m.uploadDate}</p>
                  </div>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap shrink-0">
                    <i className="ri-download-line text-xs"></i>
                    Re-download
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
